import React, { useMemo } from 'react';
import type { Participant } from '@interactive-room';

interface PodiumStageProps {
  participants: Participant[];
  size?: 'md' | 'lg';
}

const MEDALS = ['🥇', '🥈', '🥉'];
const PODIUM_HEIGHTS = { lg: ['h-44', 'h-56', 'h-36'], md: ['h-32', 'h-40', 'h-24'] } as const;

export function PodiumStage({ participants, size = 'lg' }: PodiumStageProps) {
  const ranked = useMemo(
    () =>
      [...participants]
        .filter((p) => p.role === 'student')
        .sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName)),
    [participants],
  );

  const top3 = ranked.slice(0, 3);
  const rest = ranked.slice(3);
  const order = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const heights = PODIUM_HEIGHTS[size];

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-6 sm:p-10"
      style={{
        background: 'var(--plugin-bg, var(--sala-surface-raised))',
        border: '1px solid var(--sala-border-strong)',
        color: 'var(--plugin-text, var(--sala-ink))',
        minHeight: size === 'lg' ? 420 : 320,
      }}
    >
      <div className="mb-8 text-center">
        <span
          className="inline-block rounded-full px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
          style={{ background: 'rgba(234,179,8,0.2)', color: '#eab308' }}
        >
          Podio final
        </span>
        <h2 className={`mt-3 font-display font-extrabold ${size === 'lg' ? 'text-4xl sm:text-5xl' : 'text-3xl'}`}>
          ¡Clasificación!
        </h2>
        <p className="mt-2 text-sm" style={{ color: 'var(--plugin-muted, var(--sala-ink-muted))' }}>
          {ranked.length} participante{ranked.length !== 1 ? 's' : ''} en la sala
        </p>
      </div>

      {ranked.length === 0 ? (
        <p className="text-center" style={{ color: 'var(--plugin-muted)' }}>
          Aún no hay participantes con puntuación
        </p>
      ) : (
        <>
          <div className="mx-auto flex max-w-3xl items-end justify-center gap-3 sm:gap-6">
            {order.map((p, i) => {
              const place = p === top3[0] ? 0 : p === top3[1] ? 1 : 2;
              const h = heights[place] ?? heights[0];
              const accent = place === 0 ? '#eab308' : place === 1 ? '#94a3b8' : '#d97706';
              return (
                <div key={p.participantId} className="flex flex-1 max-w-[160px] flex-col items-center">
                  <span className="text-3xl">{MEDALS[place]}</span>
                  <p className="mt-2 truncate text-center font-display text-sm font-bold sm:text-base">
                    {p.displayName}
                  </p>
                  <p className="font-display text-2xl font-extrabold tabular-nums" style={{ color: accent }}>
                    {p.score}
                  </p>
                  <div
                    className={`mt-3 flex w-full items-end justify-center rounded-t-2xl ${h} transition-all`}
                    style={{
                      background: `linear-gradient(180deg, ${accent}33 0%, ${accent}11 100%)`,
                      border: `2px solid ${accent}`,
                      boxShadow: place === 0 ? `0 0 32px ${accent}44` : undefined,
                    }}
                  >
                    <span className="mb-3 font-display text-3xl font-black opacity-80">{place + 1}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {rest.length > 0 && (
            <div className="mx-auto mt-10 max-w-md">
              <p className="mb-3 text-center text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--plugin-muted)' }}>
                Resto de la clase
              </p>
              <div className="space-y-2">
                {rest.map((p, i) => (
                  <div
                    key={p.participantId}
                    className="flex items-center gap-3 rounded-xl px-4 py-2"
                    style={{ background: 'var(--plugin-btn-bg, var(--sala-surface))' }}
                  >
                    <span className="w-6 text-center text-sm font-bold opacity-50">{i + 4}</span>
                    <span className="min-w-0 flex-1 truncate font-semibold">{p.displayName}</span>
                    <span className="font-display font-bold tabular-nums">{p.score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
